import { ComponentType } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiAlert,
  MiButton,
  MiLoader,
  MiModalConfirm,
} from "@miplatform/components";
import {
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  MODAL_TITLES,
  TYPOGRAPHY_VARIANTS,
} from "@miplatform/constants";
import { IStateManager, LoaderType } from "@miplatform/types";
import { formatOpenBalanceDisplay } from "@miplatform/utils";
import { mdiPlus, mdiTrashCanOutline } from "@mdi/js";
import { Typography } from "@mui/material";
import { Box } from "@mui/system";
import { PrebillPaymentInformation } from "../Tabs/Issues/CloseoutPrebill/PrebillPaymentInformation/PrebillPaymentInformation";
import { QCCameraItem } from "./QCCameraItem";
import {
  CloseoutAttempt,
  Invoice,
  useCloseoutQCCameraViewModel,
} from "./useCloseoutQCCameraViewModel";

type PrebillPaymentInformationProps = {
  attempt?: CloseoutAttempt;
  invoice: Invoice | null;
  setBillingCommitmentsTotal: (total: number) => void;
  getInvoice: () => Promise<Invoice | null>;
  stateManager: IStateManager;
};

const PaymentInformation =
  PrebillPaymentInformation as ComponentType<PrebillPaymentInformationProps>;

type CloseoutQCCameraProps = {
  attempt?: CloseoutAttempt;
  stateManager: IStateManager;
  isLoading?: boolean;
  getAttempt?: () => void;
  onCancel?: () => void;
};

const getVarianceColor = (variance: number) => {
  if (variance < 0) {
    return "error.main";
  }
  return "success.main";
};

export const CloseoutQCCamera = ({
  attempt,
  stateManager,
  isLoading = false,
  getAttempt = () => null,
  onCancel = () => null,
}: CloseoutQCCameraProps) => {
  const { classes, theme } = useStyles();
  const {
    invoice,
    items,
    assignmentName,
    isSummary,
    isNextDisabled,
    hasUnsavedItem,
    totalPrice,
    variance,
    billingCommitmentsTotal,
    selectedItem,
    isIncompleteIssues,
    setBillingCommitmentsTotal,
    handleGetOrCreateInvoice,
    handleAddItem,
    handleUpdateItem,
    handleDeleteRequest,
    handleDeleteModalClose,
    handleDeleteItem,
    handleBack,
    handleNext,
  } = useCloseoutQCCameraViewModel({
    attempt,
    stateManager,
    getAttempt,
    onCancel,
  });

  return (
    <Box className={classes.root}>
      <Box className={classes.loader}>
        <MiLoader isLoading={isLoading} type={LoaderType.Linear} />
      </Box>
      <Box className={classes.content}>
        {isSummary && (
          <>
            <MiAlert
              hideCloseButton
              isOpen={isIncompleteIssues}
              title="Unable to send to invoicing"
              description="All tasks must be completed before the order can be invoiced."
              variant="error"
            />
            <PaymentInformation
              attempt={attempt}
              invoice={invoice}
              setBillingCommitmentsTotal={setBillingCommitmentsTotal}
              getInvoice={handleGetOrCreateInvoice}
              stateManager={stateManager}
            />
          </>
        )}
        <Box className={classes.section}>
          <Typography className={classes.title}>{assignmentName}</Typography>
          {items.map((item) => (
            <QCCameraItem
              key={item.id}
              item={item}
              onUpdate={handleUpdateItem}
              onDelete={handleDeleteRequest}
            />
          ))}
          <Box className={classes.sectionFooter}>
            <Typography
              className={classes.totalLabel}
              variant={TYPOGRAPHY_VARIANTS.BODY1}
            >
              Total Price:
            </Typography>
            <Typography className={classes.totalValue}>
              {formatOpenBalanceDisplay(totalPrice)}
            </Typography>
          </Box>
        </Box>
        {!isSummary && (
          <Box className={classes.addItemRow}>
            <MiButton
              title="Add Item"
              startIcon={{ path: mdiPlus }}
              variant={BUTTON_VARIANTS.OUTLINED}
              size="small"
              disabled={isLoading || hasUnsavedItem}
              onClick={handleAddItem}
            />
          </Box>
        )}
      </Box>
      <Box className={classes.footer}>
        {isSummary && (
          <Box className={classes.summaryTotals}>
            <Typography
              className={classes.totalLabel}
              variant={TYPOGRAPHY_VARIANTS.BODY1}
            >
              Total Price:
            </Typography>
            <Typography className={classes.totalValue}>
              {formatOpenBalanceDisplay(totalPrice)}
            </Typography>
            <Typography
              className={classes.totalLabel}
              variant={TYPOGRAPHY_VARIANTS.BODY1}
            >
              Total Paid:
            </Typography>
            <Typography className={classes.totalValue}>
              {formatOpenBalanceDisplay(billingCommitmentsTotal)}
            </Typography>
            <Typography
              className={classes.totalLabel}
              variant={TYPOGRAPHY_VARIANTS.BODY1}
            >
              Variance:
            </Typography>
            <Typography
              className={classes.totalValue}
              color={getVarianceColor(variance)}
            >
              {formatOpenBalanceDisplay(variance)}
            </Typography>
          </Box>
        )}
        <Box className={classes.actions}>
          <MiButton
            className={classes.cancelBtn}
            title={isSummary ? "Return: Pre-Bill" : "Cancel"}
            variant={BUTTON_VARIANTS.TEXT}
            onClick={handleBack}
            disabled={isLoading}
          />

          {!isSummary && (
            <MiButton
              className={classes.confirmBtn}
              title="Pricing Summary"
              disabled={isLoading || isNextDisabled}
              variant={BUTTON_VARIANTS.CONTAINED}
              onClick={handleNext}
            />
          )}

          {isSummary && (
            <MiButton
              className={classes.confirmBtn}
              title="Send to Invoicing"
              disabled={isLoading || isNextDisabled || isIncompleteIssues}
              variant={BUTTON_VARIANTS.CONTAINED}
              onClick={handleNext}
            />
          )}
        </Box>
      </Box>
      <MiModalConfirm
        title={MODAL_TITLES.DELETE}
        open={Boolean(selectedItem)}
        disableClick={isLoading}
        titleIcon={{
          path: mdiTrashCanOutline,
          color: theme.palette.error.main,
        }}
        textPositive={BUTTON_LABELS.DELETE}
        handleNegative={handleDeleteModalClose}
        handlePositive={handleDeleteItem}
        description="Are you sure you want to delete this invoice item? This action cannot be undone."
      />
    </Box>
  );
};

CloseoutQCCamera.displayName = "CloseoutQCCamera";

const useStyles = makeStyles()((theme) => ({
  root: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  loader: {
    position: "absolute",
    width: "100%",
    top: 0,
    left: 0,
  },
  content: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  title: {
    fontWeight: theme.fontWeight.bold,
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    backgroundColor: theme.palette.grey.light,
    padding: theme.spacing(2, 3),
    border: `1px solid ${theme.palette.grey.accent}`,
    borderRadius: theme.shape.borderRadius,
  },
  sectionFooter: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    backgroundColor: theme.palette.common.white,
    flexWrap: "wrap",
    borderTop: `1px solid ${theme.palette.grey.darkAccent}`,
    borderBottomLeftRadius: theme.shape.borderRadius,
    borderBottomRightRadius: theme.shape.borderRadius,
    width: "100%",
    gap: theme.spacing(2),
    padding: theme.spacing(4),
  },
  totalLabel: {
    fontWeight: theme.fontWeight.bold,
    color: theme.palette.grey.medium,
  },
  totalValue: {
    fontWeight: theme.fontWeight.semiBold,
  },
  addItemRow: {
    display: "flex",
    justifyContent: "flex-end",
    padding: theme.spacing(3, 0),
  },
  summaryTotals: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(4),
    paddingTop: theme.spacing(2),
    paddingRight: theme.spacing(4),
  },
  footer: {
    backgroundColor: theme.palette.common.white,
    marginTop: "auto",
    position: "sticky",
    bottom: 0,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(4),
    padding: theme.spacing(2, 0),
    zIndex: theme.zIndex.drawer,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(4),
  },
  cancelBtn: {
    color: theme.palette.common.black,
    fontWeight: theme.fontWeight.bold,
    textDecoration: "underline",
    textUnderlineOffset: theme.spacing(1),
  },
  confirmBtn: {
    fontWeight: theme.fontWeight.bold,
  },
}));
